import { Directive, HostListener, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { PreviewQuestionAnswerDetailsSaved } from './buyer-preview.models';
import { BuyerPreviewService } from './buyer-preview.service';
import { ValidateResponseService } from './multi-section-form-preview/validate-response.service';
import { validateAttachment } from '../shared/attachment-validation';
import Swal from 'sweetalert2';

@Directive({
  selector: '[listenClick]',
  standalone: true,
})
export class ListenClickDirective {
  sanitizer = inject(DomSanitizer);
  buyerPreviewService = inject(BuyerPreviewService);
  vrs = inject(ValidateResponseService);
  @HostListener('click', ['$event'])
  @HostListener('keydown', ['$event'])
  @HostListener('keypress', ['$event'])
  @HostListener('keyup', ['$event'])
  setHelper(e: Event) {
    if (
      (e as PointerEvent).type === 'click' ||
      (['keypress', 'keydown', 'keyup'].includes((e as KeyboardEvent).type) &&
        (e as KeyboardEvent).key === 'Tab')
    ) {
      this.locallySaveInputElementValue(e as PointerEvent);
    }
  }

  private _formLayout = '';
  public get formLayout() {
    let localStorageFormLayout = localStorage.getItem('FormLayout');
    return (this._formLayout = localStorageFormLayout ?? '');
  }

  private _orgFormId = '';
  public get orgFormId() {
    let localStorageOrgFormId = localStorage.getItem('OrgFormId');
    return (this._orgFormId = localStorageOrgFormId ?? '');
  }

  swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn',
    },
    buttonsStyling: false,
  });
  locallySaveInputElementValue(e: PointerEvent) {
    if ((e.target as HTMLInputElement).tagName === 'SELECT') {
      this.handleSingleSelectDropdown(e);
    } else if ((e.target as HTMLInputElement).tagName === 'INPUT') {
      const eventTarget = e.target as HTMLInputElement;
      if (eventTarget.type === 'date') {
        this.handleDateInput(eventTarget);
      } else if (eventTarget.type === 'number') {
        this.handleNumberInput(eventTarget);
      } else if (['text', 'email', 'tel'].includes(eventTarget.type)) {
        if (eventTarget.id.includes('submitter__')) {
          eventTarget.addEventListener('input', (textInputEvent) => {
            setTimeout(() => {
              this.handleSubmitterInput(eventTarget);
            }, 500);
          });
          this.handleSubmitterInput(eventTarget);
        } else {
          this.handleTextInput(eventTarget);
        }
      } else if (eventTarget.type === 'checkbox') {
        this.handleCheckBoxInput(e);
      } else if (eventTarget.type === 'radio') {
        this.handleRadioInput(e);
      } else if (eventTarget.type === 'file') {
        this.handleFileInput(eventTarget);
      }
    } else if (
      (e.target as HTMLOptionElement).tagName === 'OPTION' &&
      ((e.target as HTMLOptionElement).parentElement as HTMLSelectElement)
        .multiple
    ) {
      this.handleMultiSelectDropdown(e);
    } else if ((e.target as HTMLTextAreaElement).tagName === 'TEXTAREA') {
      this.handleTextAreaInput(e);
    }
  }

  handleRadioInput(e: PointerEvent) {
    const eventTarget = e.target as HTMLInputElement;
    const selectedRadioEle = eventTarget;

    const isChecked = eventTarget.checked;
    const selection = isChecked ? (eventTarget.nextSibling as Text).data : null;

    if (selection && isChecked) {
      selectedRadioEle.setAttribute('checked', 'true');
      selectedRadioEle.checked = true;
    } else {
      selectedRadioEle.removeAttribute('selected');
      selectedRadioEle.checked = false;
    }
    //apply checked attribute to the selected Radio

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(eventTarget.name.replace('Radio_', '').toString()),
    );

    const latestFieldAnswerValue = selection ? [selection] : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML,
      ),
      answerUniqueId: [eventTarget.value],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };

    localStorage.setItem(
      eventTarget.name.replace('Radio_', '').toString(),
      JSON.stringify(fieldAnswer),
    );

    this.buyerPreviewService.helper$.next(
      'clicked on preview question input element',
    );
  }


  fileName = ''
  fileExtension = ''
  selectedFile: File | null = null;
  handleFileInput(eventTarget: HTMLInputElement) {

    const mode = localStorage.getItem('mode') || null
    let count = 0
    eventTarget.addEventListener('change', (fileInputEvent) => {
      const file = eventTarget.files?.[0];
      if (!file) return;

      count++;

      // SM-3232: Enforce the 5 MB / PDF-Word-Excel rule shown in the question note
      // before any FileReader work or network call. Reject invalid files up front
      // so the question is never marked as a Valid Response.
      const validation = validateAttachment(file);
      if (validation.valid === false) {
        eventTarget.value = '';
        eventTarget.removeAttribute('value');
        this.fileName = '';
        this.fileExtension = '';
        this.selectedFile = null;
        this.swalWithBootstrapButtons.fire({
          title: validation.title,
          text: validation.message,
          icon: 'error',
          confirmButtonText: 'OK',
        });
        count--;
        return;
      }

      this.fileName = file.name;
      this.fileExtension = file.name.split('.').pop() || '';
      const reader = new FileReader();
      if (count == 1) {
        reader.onload = (e) => {

          const blob = new Blob([e.target.result]);

          // Log the blob as binary data
          const reader2 = new FileReader();
          reader2.onloadend = () => {
            eventTarget.removeAttribute('value');

            eventTarget.setAttribute('value', reader2.result ? 'FILE_UPLOADED' : '');
            const modifiedFieldEle = document.createElement('div');
            modifiedFieldEle.setAttribute(
              'style',
              'display:flex;justify-content:left',
            );
            modifiedFieldEle.innerHTML = eventTarget.outerHTML;

            const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
              localStorage.getItem(eventTarget.id.replace('fileID_', '').toString()),
            );
            this.selectedFile = eventTarget.files[0];
            if (this.selectedFile && mode == 'supplierEdit') {
              const formData_Scan = new FormData();
              formData_Scan.append('attachmentFile', this.selectedFile, this.selectedFile.name);
              this.buyerPreviewService.ProcessFileScan(formData_Scan).subscribe({
                next: (resp) => {

                  if (resp.IsSuccess) {
                    const formData = new FormData();
                    formData.append('id', this.orgFormId);
                    formData.append('FormLayout', this.formLayout);
                    formData.append('File', this.selectedFile, this.selectedFile.name)
                    formData.append('questionUniqueId', eventTarget.id.replace('fileID_', '').toString());
                    formData.append('JsonResponse', JSON.stringify([{
                      id: eventTarget.id.replace('fileID_', '').toString(),
                      answers: this.selectedFile.name
                    }]));
                    this.buyerPreviewService.SaveUserSurveyAttachmentResponse(formData).subscribe({
                      next: (resp) => {
                        // SM-3232: The server is the authority on the size/type rule.
                        // If it rejects the file, surface the error and do NOT mark
                        // the question as a Valid Response.
                        if (resp?.IsSuccess !== true) {
                          eventTarget.value = '';
                          eventTarget.removeAttribute('value');
                          this.fileName = '';
                          this.fileExtension = '';
                          this.selectedFile = null;
                          this.swalWithBootstrapButtons.fire({
                            title: 'Failure!',
                            text: resp?.Message || 'Failed to upload attachment. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                          });
                          count--;
                          return;
                        }

                        const latestFieldAnswerValue = this.selectedFile.name
                          ? [this.selectedFile.name]
                          : null;

                        const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
                          modifiedField: this.sanitizer.bypassSecurityTrustHtml(
                            modifiedFieldEle.outerHTML,
                          ),
                          answerUniqueId: [eventTarget.id],
                          answerValue: latestFieldAnswerValue,
                          isAnswerValid: Boolean(eventTarget.value),
                          isAnswerModified: !(
                            oldFieldAnswer?.answerValue?.toString() ===
                            latestFieldAnswerValue?.toString()
                          ),
                        };

                        localStorage.setItem(eventTarget.id.replace('fileID_', '').toString(), JSON.stringify(fieldAnswer));

                        const activeFieldId = eventTarget.id.replace('fileID_', '');

                        this.buyerPreviewService.helper$.next(
                          'clicked on preview question input element',
                        );
                      },
                      error: () => {
                        this.swalWithBootstrapButtons
                          .fire({
                            title: 'Failure!',
                            text: 'Failed to upload attachment. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                          })
                          .then(() => { });
                      },
                    });
                  }
                  else {
                    this.swalWithBootstrapButtons
                      .fire({
                        title: 'Failure!',
                        text:
                          !resp.IsSuccess && resp.Message == ''
                            ? 'Some error occured'
                            : resp.Message,
                        icon: 'error',
                        confirmButtonText: 'OK',
                      })
                      .then(() => { });
                  }
                }
              })
            } else {
              const latestFieldAnswerValue = this.selectedFile.name
                ? [this.selectedFile.name]
                : null;

              const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
                modifiedField: this.sanitizer.bypassSecurityTrustHtml(
                  modifiedFieldEle.outerHTML,
                ),
                answerUniqueId: [eventTarget.id],
                answerValue: latestFieldAnswerValue,
                isAnswerValid: Boolean(eventTarget.value),
                isAnswerModified: !(
                  oldFieldAnswer?.answerValue?.toString() ===
                  latestFieldAnswerValue?.toString()
                ),
              };

              localStorage.setItem(eventTarget.id.replace('fileID_', '').toString(), JSON.stringify(fieldAnswer));
              const activeFieldId = eventTarget.id.replace('fileID_', '');

              this.buyerPreviewService.helper$.next(
                'clicked on preview question input element',
              );
            }
          };
          reader2.readAsBinaryString(blob);
        };
      }
      reader.readAsArrayBuffer(file);
    });
  }

  handleCheckBoxInput(e: PointerEvent) {
    const dynamicCheckboxLabelPrefix = /Checkbox_Label(\d+)_/g;
    const eventTarget = e.target as HTMLInputElement;

    const checkboxWrapperTarget =
      eventTarget?.parentElement?.parentElement?.parentElement;

    checkboxWrapperTarget.setAttribute(
      'id',
      eventTarget.parentElement.id
        ?.replace(dynamicCheckboxLabelPrefix, 'Checkbox_WRAPPER_')
        ?.toString(),
    );

    const allCheckboxContainersArr = Array.from(checkboxWrapperTarget.children);

    const allCheckboxesArr = [...allCheckboxContainersArr]?.map(
      (x) => x?.firstChild?.firstChild as HTMLInputElement,
    );

    if (allCheckboxesArr?.length !== 0) {
      const checkedOptionsArr = allCheckboxesArr.filter((cb) => cb.checked);

      // remove select attribute for all the existing options
      allCheckboxesArr.forEach((option: HTMLInputElement) => {
        option.removeAttribute('checked');
        option.checked = false;
      });

      //add select attribute for all the checked options
      checkedOptionsArr.forEach((checkedOptionEle: HTMLInputElement) => {
        checkedOptionEle.setAttribute('checked', 'true');
        checkedOptionEle.checked = true;
      });

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left',
      );
      modifiedFieldEle.innerHTML = checkboxWrapperTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString(),
        ),
      );

      const latestFieldAnswerValue = checkedOptionsArr.map(
        (item) => (item.nextSibling as Text).data,
      );

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML,
        ),
        answerUniqueId: checkedOptionsArr.map((item) => item.id),
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(checkedOptionsArr?.length),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString(),
        JSON.stringify(fieldAnswer),
      );

      this.buyerPreviewService.helper$.next(
        'clicked on preview question input element',
      );
    }
  }

  handleSingleSelectDropdown(e: PointerEvent) {
    const eventTarget = e.target as HTMLSelectElement;
    const optionsArr = Array.from(eventTarget.options as HTMLOptionsCollection);
    const selectedOptionEle = optionsArr.find(
      (option: HTMLOptionElement) => option.value === eventTarget.value,
    );

    const selection = selectedOptionEle.text;

    //apply selected attribute to the selected option
    optionsArr.forEach((option: HTMLOptionElement) => {
      option.removeAttribute('selected');
      option.selected = false;
    });

    if (selection.toLowerCase() !== 'select option') {
      selectedOptionEle.setAttribute('selected', 'true');
      selectedOptionEle.selected = true;
    }
    //apply selected attribute to the selected option

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(eventTarget.id.replace('Dropdown_', '').toString()),
    );

    const latestFieldAnswerValue =
      selection.toLowerCase() !== 'select option' ? [selection] : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML,
      ),
      answerUniqueId: [eventTarget.value],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };

    localStorage.setItem(
      eventTarget.id.replace('Dropdown_', '').toString(),
      JSON.stringify(fieldAnswer),
    );

    this.buyerPreviewService.helper$.next(
      'clicked on preview question input element',
    );
  }

  handleMultiSelectDropdown(e: PointerEvent) {
    const multiSelectTarget = (e.target as HTMLOptionElement)
      .parentElement as HTMLSelectElement;
    const allOptionsArr = Array.from(
      multiSelectTarget.options as HTMLOptionsCollection,
    );
    if (multiSelectTarget.selectedOptions) {
      const selectedOptionsArr = Array.from(
        multiSelectTarget.selectedOptions as HTMLOptionsCollection,
      );

      // remove select attribute for all the existing options
      allOptionsArr.forEach((option: HTMLOptionElement) => {
        option.removeAttribute('selected');
        option.selected = false;
      });

      //add select attribute for all the selected options
      selectedOptionsArr.forEach((selectedOptionEle: HTMLOptionElement) => {
        selectedOptionEle.setAttribute('selected', 'true');
        selectedOptionEle.selected = true;
      });

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left',
      );
      modifiedFieldEle.innerHTML = multiSelectTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          multiSelectTarget.id.replace('MultiselectDropdown_', '').toString(),
        ),
      );

      const latestFieldAnswerValue = selectedOptionsArr.map(
        (item) => item.text,
      );

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML,
        ),
        answerUniqueId: selectedOptionsArr.map((item) => item.value),
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(multiSelectTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        multiSelectTarget.id.replace('MultiselectDropdown_', '').toString(),
        JSON.stringify(fieldAnswer),
      );

      this.buyerPreviewService.helper$.next(
        'clicked on preview question input element',
      );
    }
  }

  handleDateInput(eventTarget: HTMLInputElement) {
    eventTarget.addEventListener('input', (dateInputEvent) => {

      eventTarget.removeAttribute('value');

      eventTarget.setAttribute('value', eventTarget.value);
      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left',
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(eventTarget.id.toString()),
      );

      const latestFieldAnswerValue = eventTarget.value
        ? [eventTarget.value]
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML,
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(eventTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(eventTarget.id, JSON.stringify(fieldAnswer));

      this.buyerPreviewService.helper$.next(
        'clicked on preview question input element',
      );
    });
  }

  handleNumberInput(eventTarget: HTMLInputElement) {
    eventTarget.addEventListener('blur', (numberInputEvent) => {
      eventTarget.removeAttribute('value');
      eventTarget.setAttribute('value', eventTarget.value);
      const modifiedFieldEle = document.createElement('div');
      if (eventTarget.validity.valid) {
        modifiedFieldEle.setAttribute(
          'style',
          'display:flex; justify-content:left',
        );
      } else {
        modifiedFieldEle.setAttribute('style', 'text-align:left');
      }
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const invalidAnswerMsgHTML = eventTarget.validity.valid
        ? ''
        : `<small style="display:block; color: crimson; margin-top: 0.5rem">Value should be in the range of ${eventTarget.min} and ${eventTarget.max}</small>`;
      modifiedFieldEle.innerHTML =
        modifiedFieldEle.innerHTML + invalidAnswerMsgHTML;

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          eventTarget.id.replace('textboxID_', '').toString(),
        ),
      );

      const latestFieldAnswerValue = eventTarget.value
        ? [eventTarget.value]
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML,
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: eventTarget.validity.valid,
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        eventTarget.id.replace('textboxID_', ''),
        JSON.stringify(fieldAnswer),
      );

      this.buyerPreviewService.helper$.next(
        'clicked on preview question input element',
      );
    });
  }

  handleTextInput(eventTarget: HTMLInputElement) {
    eventTarget.addEventListener('blur', (textInputEvent) => {

      eventTarget.removeAttribute('value');

      eventTarget.setAttribute('value', eventTarget.value);
      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left',
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const inputValidationResult = eventTarget.value !== ''
        ? this.vrs.validateInput(eventTarget)
        : { isInputValid: true, messageHTML: '' };

      const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          eventTarget.id.replace('textboxID_', '').toString(),
        ),
      );

      const latestFieldAnswerValue = eventTarget.value
        ? [eventTarget.value]
        : null;

      const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML + inputValidationResult.messageHTML,
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: inputValidationResult.isInputValid,
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        eventTarget.id.replace('textboxID_', ''),
        JSON.stringify(fieldAnswer),
      );

      this.buyerPreviewService.helper$.next(
        'clicked on preview question input element',
      );
    });
  }

  handleSubmitterInput(eventTarget: HTMLInputElement) {
    localStorage.setItem(
      eventTarget.id.replace('submitter__textboxID_', ''),
      eventTarget.value,
    );
    this.buyerPreviewService.helper$.next(eventTarget.id);
  }

  handleTextAreaInput(e: PointerEvent) {
    const textAreaTarget = e.target as HTMLTextAreaElement;
    let timer;
    textAreaTarget.addEventListener('keyup', () => {
      // Clear the timer if the user types
      clearTimeout(timer);

      // Set up a new timer to fire after 1K milliseconds
      timer = setTimeout(() => {
        // The user has stopped typing
        this.onTextAreaInputComplete(textAreaTarget);
      }, 1170);
    });
  }

  onTextAreaInputComplete(eventTarget: HTMLTextAreaElement) {
    eventTarget.removeAttribute('value');

    eventTarget.setAttribute('value', eventTarget.value);
    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;

    const oldFieldAnswer: PreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(
        eventTarget.id.replace('textareaID_', '').toString(),
      ),
    );

    const latestFieldAnswerValue = eventTarget.value
      ? [eventTarget.value]
      : null;

    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: this.sanitizer.bypassSecurityTrustHtml(
        modifiedFieldEle.outerHTML,
      ),
      answerUniqueId: [eventTarget.id],
      answerValue: latestFieldAnswerValue,
      isAnswerValid: Boolean(eventTarget.value),
      isAnswerModified: !(
        oldFieldAnswer?.answerValue?.toString() ===
        latestFieldAnswerValue?.toString()
      ),
    };
    localStorage.setItem(
      eventTarget.id.replace('textareaID_', ''),
      JSON.stringify(fieldAnswer),
    );

    this.buyerPreviewService.helper$.next(
      'clicked on preview question input element',
    );
  }
}
